/**
 * /pos/customer-display — operator opens this on a tablet facing the customer.
 *
 * Subscribes to /api/realtime/connect and filters POS_CART_UPDATED events
 * by the session_id query param. On first paint we hydrate from
 * /api/pos/customer-display/snapshot?session_id=... so the screen doesn't
 * flash blank during the WebSocket connect.
 *
 * Two view states keyed by `kind`:
 *   - 'cart'      — live line items + running total.
 *   - 'completed' — "Thank you!" + receipt summary for ~10s, then back to cart.
 *
 * No POS write capability — the page only reads broadcasts. Gated by the same
 * `pos.session.read` route the snapshot endpoint requires.
 */

import { ApiError } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { RealtimeEvent } from '@/lib/types';
import { useRealtime } from '@/lib/useRealtime';
import { CheckCircle2, ShoppingBag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface Snapshot {
  sessionId: string;
  kind: 'cart' | 'completed';
  currency: string;
  subtotalMinor: number;
  totalMinor: number;
  cartLines: Array<{
    description: string;
    quantity: number;
    unitPriceMinor: number;
    lineTotalMinor: number;
  }>;
  transactionId: string | null;
  updatedAt: string;
}

const COMPLETED_VIEW_MS = 10_000;

async function fetchSnapshot(sessionId: string): Promise<Snapshot> {
  const res = await fetch(
    `/api/pos/customer-display/snapshot?session_id=${encodeURIComponent(sessionId)}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    throw new ApiError('snapshot_failed', res.status);
  }
  const json = (await res.json()) as { snapshot: Snapshot };
  return json.snapshot;
}

export function PosCustomerDisplayPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const sessionId = params.get('session_id') ?? '';
  const greeting = params.get('greeting') ?? 'Welcome';

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  // First-paint hydrate.
  useEffect(() => {
    if (!sessionId) return;
    void fetchSnapshot(sessionId)
      .then(setSnapshot)
      .catch(() => setError('Could not load cart. Reload the page.'));
  }, [sessionId]);

  // Auto-reset 'completed' view back to a clean cart after COMPLETED_VIEW_MS.
  useEffect(() => {
    if (!snapshot || snapshot.kind !== 'completed') return;
    const timer = window.setTimeout(() => {
      setSnapshot((prev) =>
        prev
          ? {
              ...prev,
              kind: 'cart',
              cartLines: [],
              subtotalMinor: 0,
              totalMinor: 0,
              transactionId: null,
              updatedAt: new Date().toISOString(),
            }
          : prev,
      );
    }, COMPLETED_VIEW_MS);
    return () => window.clearTimeout(timer);
  }, [snapshot]);

  useRealtime((event: RealtimeEvent) => {
    if (event.type !== 'POS_CART_UPDATED') return;
    if (event.session_id !== sessionId) return;
    setSnapshot({
      sessionId,
      kind: event.kind,
      currency: event.currency,
      subtotalMinor: event.subtotal_minor,
      totalMinor: event.total_minor,
      cartLines: event.cart_lines.map((l) => ({
        description: l.description,
        quantity: l.quantity,
        unitPriceMinor: l.unit_price_minor,
        lineTotalMinor: l.line_total_minor,
      })),
      transactionId: event.transaction_id,
      updatedAt: event.emitted_at,
    });
  });

  if (!sessionId) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background p-8">
        <p className="text-sm text-muted-foreground">
          Missing <code>session_id</code>. Open from the till's "Customer display" button.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background p-8">
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      </div>
    );
  }

  const currency = snapshot?.currency ?? 'GBP';

  if (snapshot?.kind === 'completed') {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background p-12 text-center">
        <CheckCircle2 className="size-24 text-success" strokeWidth={1.25} />
        <h1 className="mt-8 font-heading text-5xl font-medium tracking-tight">Thank you</h1>
        <p className="mt-4 text-lg text-muted-foreground">Receipt total</p>
        <p className="num mt-2 text-6xl font-medium">
          {formatPriceMinor(snapshot.totalMinor, currency)}
        </p>
        {snapshot.transactionId && (
          <p className="num mt-6 text-xs text-muted-foreground">
            #{snapshot.transactionId.slice(-8).toUpperCase()}
          </p>
        )}
      </div>
    );
  }

  const lines = snapshot?.cartLines ?? [];
  const empty = lines.length === 0;

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border px-12 py-8">
        <p className="text-base text-muted-foreground">{greeting}</p>
        <h1 className="mt-1 font-heading text-3xl font-medium tracking-tight">Your purchase</h1>
      </div>

      {/* Cart lines */}
      <div className="flex-1 overflow-y-auto px-12 py-6">
        {empty ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <ShoppingBag className="size-16 text-muted-foreground/40" strokeWidth={1.5} />
            <p className="mt-6 text-lg text-muted-foreground">
              Your items will appear here as they are rung up.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {lines.map((l, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: customer-display line position is stable per cart
                key={`${l.description}-${i}`}
                className="flex items-center justify-between py-4"
              >
                <div className="flex items-baseline gap-3">
                  <span className="num text-lg text-muted-foreground">{l.quantity}×</span>
                  <span className="text-lg">{l.description || '(item)'}</span>
                </div>
                <span className="num text-lg font-medium">
                  {formatPriceMinor(l.lineTotalMinor, currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Total */}
      <div className="border-t border-border bg-surface px-12 py-8">
        <div className="flex items-baseline justify-between">
          <span className="text-xl text-muted-foreground">Total</span>
          <span className="num text-4xl font-medium">
            {formatPriceMinor(snapshot?.totalMinor ?? 0, currency)}
          </span>
        </div>
      </div>
    </div>
  );
}
